import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SettingField } from "@/components/settings/setting-field";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { useTeamMembers } from "../hooks";
import { useTeamManagementUIStore } from "../store/use-team-management-ui-store";
import type { TeamMemberFormData } from "../types";

// Enhanced form validation schema with optional email
const createTeamMemberSchema = (t: (key: string) => string) =>
  z.object({
    displayName: z.string().min(1, t("validation.displayNameRequired")),
    bio: z.string().max(500, t("validation.bioMaxLength")).optional(),
    canAcceptOnlineBookings: z.boolean(),
    isActive: z.boolean(),
    color: z.string().default("#3B82F6"),

    // Optional email field - when provided, invitation will be sent automatically
    email: z.string().email(t("validation.emailRequired")).optional().or(z.literal("")),

    // Salon role assignment
    salonRole: z.enum(["WORKER", "MANAGER", "OWNER"]).optional(),
  });

const createUpdateTeamMemberSchema = (t: (key: string) => string) =>
  z.object({
    displayName: z.string().min(1, t("validation.displayNameRequired")),
    bio: z.string().max(500, t("validation.bioMaxLength")).optional(),
    isActive: z.boolean(),
    color: z.string().default("#3B82F6"),
  });

export function TeamMemberForm() {
  const { t } = useTranslation("settings");

  const {
    isCreateDialogOpen,
    isEditDialogOpen,
    editingMemberId,
    closeCreateDialog,
    closeEditDialog,
  } = useTeamManagementUIStore();

  const { createMember, updateMember, creating, updating, teamMembers } = useTeamMembers();

  // Find the member being edited
  const editingMember = teamMembers.find((m) => m.id === editingMemberId);

  const isOpen = isCreateDialogOpen || isEditDialogOpen;
  const isEditing = isEditDialogOpen && !!editingMemberId;
  const isLoading = creating || updating;

  // Form setup with conditional schema based on mode
  const formSchema = isEditing ? createUpdateTeamMemberSchema(t) : createTeamMemberSchema(t);
  const form = useForm<TeamMemberFormData>({
    resolver: zodResolver(formSchema) as Resolver<TeamMemberFormData>,
    defaultValues: {
      displayName: "",
      bio: "",
      canAcceptOnlineBookings: true,
      isActive: true,
      color: "#3B82F6",
      email: "",
      salonRole: "WORKER",
    },
  });

  // Reset form when member data loads for editing or in create mode
  useEffect(() => {
    if (isEditing && editingMember) {
      form.reset({
        displayName: editingMember.worker?.displayName || "",
        bio: editingMember.worker?.bio || "",
        isActive: editingMember.isActive,
        color: "#3B82F6", // Always use default color
      });
    } else if (!isEditing) {
      // Reset for create mode
      form.reset({
        displayName: "",
        bio: "",
        canAcceptOnlineBookings: true,
        isActive: true,
        color: "#3B82F6",
        email: "",
        salonRole: "WORKER",
      });
    }
  }, [isEditing, editingMember, form]);

  const handleClose = () => {
    if (isCreateDialogOpen) {
      closeCreateDialog();
    } else {
      closeEditDialog();
    }
    form.reset();
  };

  const onSubmit = async (data: TeamMemberFormData) => {
    try {
      if (isEditing && editingMemberId) {
        // For update
        await updateMember(editingMemberId, data);
      } else {
        // For create - data now contains only worker profile fields
        await createMember(data);
      }
      handleClose();
    } catch (error) {
      // Error is handled by the mutation hook
      console.error("Team member form submission error:", error);
    }
  };

  const title = isEditing
    ? `${t("actions.edit")} ${t("teamManagement.memberName")}`
    : t("teamManagement.addMember");
  const submitText = isEditing ? t("actions.update") : t("actions.add");

  // Helper function to get translated role name
  const getRoleDisplayName = (role: string | undefined) => {
    if (!role) return t("placeholders.selectOption");

    const roleMap: Record<string, string> = {
      WORKER: t("teamManagement.worker"),
      MANAGER: t("teamManagement.manager"),
      OWNER: t("teamManagement.owner"),
    };
    return roleMap[role] || role;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("teamManagement.memberName")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("placeholders.enterName")}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Salon Role Selection - Only for Create Mode */}
            {!isEditing && (
              <FormField
                control={form.control}
                name="salonRole"
                render={({ field }) => (
                  <FormItem>
                    <SettingField
                      label={t("teamManagement.role")}
                      helpText={t("descriptions.rolePermissions")}
                    >
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("placeholders.selectOption")}>
                              {getRoleDisplayName(field.value)}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="WORKER">{t("teamManagement.worker")}</SelectItem>
                          <SelectItem value="MANAGER">{t("teamManagement.manager")}</SelectItem>
                          <SelectItem value="OWNER">{t("teamManagement.owner")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </SettingField>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("labels.bio")} ({t("labels.optional")})
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("placeholders.enterBio")}
                      rows={3}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            {/* Email Section - Only for Create Mode */}
            {!isEditing && (
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("business.email")} ({t("labels.optional")})
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder={t("placeholders.enterEmail")}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Spinner className="w-4 h-4 mr-2" />}
                {submitText}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
